import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Alert, Button, Card, Collapse, Descriptions, Form, Input, Modal, Spin, message } from "antd"
import { FileTextOutlined, TableOutlined, UserOutlined } from "@ant-design/icons"
import { useNavigate, useParams } from "react-router-dom"
import { TempLayout } from "@/layouts"
import { usaSapAssetStore } from "@/stores/sap-asset/sapAssetStore"
import { ISapAssetDetail } from "@/types/sapAsset.type"
import AssetPayloadEditor from "../../features/sap-assets/components/AssetPayloadEditor"
import { getAssetEditAccess } from "../../features/sap-assets/utils/assetEditAccess"
import { getAssetEditDraft, removeAssetEditDraft, saveAssetEditDraft } from "../../features/sap-assets/utils/assetEditDraft"
import { expandAssetRowsForNumbering } from "../../features/sap-assets/utils/assetNumbering"
import { isAssetTemplateV2 } from "../../features/sap-assets/utils/assetColumns"

const SapAssetsEditPage = () => {
  const { recordId } = useParams()
  const navigate = useNavigate()
  const [commentForm] = Form.useForm()
  const [assets, setAssets] = useState<ISapAssetDetail[]>([])
  const [initialSnapshot, setInitialSnapshot] = useState<string>("")
  const [hasInitialized, setHasInitialized] = useState(false)
  const [isCommentModalVisible, setIsCommentModalVisible] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const draftPromptKeyRef = useRef<string | null>(null)
  const { updateSapAsset, fetchSapAssetById, currentAsset, isLoading, error } = usaSapAssetStore()

  const currentEmployeeId = useMemo(() => {
    try {
      const currentSession = JSON.parse(window.localStorage.getItem("session_farms") || "null") as { employee_id?: string } | null
      return currentSession?.employee_id || null
    } catch (_error) {
      return null
    }
  }, [])

  const isApproved = (currentAsset?.approval_status || "").toUpperCase() === "APPROVED"
  const editAccess = useMemo(
    () => getAssetEditAccess(currentAsset?.approval_process_id, currentEmployeeId),
    [currentAsset?.approval_process_id, currentEmployeeId],
  )
  const isReadOnly = isApproved || !editAccess.canEdit
  const assetRecordId = currentAsset?.record_id || null
  const assetUpdatedAt = currentAsset?.updated_at || null
  const assetDetail = useMemo(
    () => (Array.isArray(currentAsset?.detail) ? currentAsset.detail : []),
    [currentAsset?.detail],
  )
  const isNumberingMode = useMemo(
    () => isAssetTemplateV2(currentAsset?.template_key, assetDetail),
    [currentAsset?.template_key, assetDetail],
  )
  const editableAssetDetail = useMemo(
    () => expandAssetRowsForNumbering(assetDetail, currentAsset?.template_key),
    [assetDetail, currentAsset?.template_key],
  )
  const hasExpandedRows = editableAssetDetail.length > assetDetail.length

  const currentSnapshot = useMemo(() => JSON.stringify({ assets }), [assets])
  const isDirty = hasInitialized && currentSnapshot !== initialSnapshot

  const applyWorkingCopy = useCallback((nextAssets: ISapAssetDetail[]) => {
    setAssets(nextAssets)
  }, [])

  const fetchAsset = useCallback(async () => {
    if (recordId) {
      await fetchSapAssetById(recordId)
    }
  }, [recordId, fetchSapAssetById])

  useEffect(() => {
    void fetchAsset()
  }, [fetchAsset])

  useEffect(() => {
    if (!assetRecordId) return

    const backendSnapshot = JSON.stringify({ assets: editableAssetDetail })
    const persistedDraft = getAssetEditDraft(assetRecordId)
    const backendUpdatedAt = assetUpdatedAt
    const conflictKey = `${assetRecordId}:${persistedDraft?.savedAt || "none"}:${backendUpdatedAt || "none"}`

    applyWorkingCopy(editableAssetDetail)
    setInitialSnapshot(backendSnapshot)
    setHasInitialized(true)

    if (isReadOnly) {
      draftPromptKeyRef.current = conflictKey
      return
    }

    if (!persistedDraft) {
      draftPromptKeyRef.current = conflictKey
      return
    }

    if (persistedDraft.baseUpdatedAt === backendUpdatedAt) {
      applyWorkingCopy(persistedDraft.assets)
      draftPromptKeyRef.current = conflictKey
      return
    }

    if (draftPromptKeyRef.current === conflictKey) {
      return
    }

    draftPromptKeyRef.current = conflictKey
    Modal.confirm({
      title: "Restore unsaved draft?",
      content: "A local draft exists from an older server version. Restore it to continue editing, or discard it and use the latest server data.",
      okText: "Restore Draft",
      cancelText: "Discard Draft",
      onOk: () => applyWorkingCopy(persistedDraft.assets),
      onCancel: () => removeAssetEditDraft(assetRecordId),
    })
  }, [assetRecordId, assetUpdatedAt, editableAssetDetail, isReadOnly, applyWorkingCopy])

  useEffect(() => {
    if (!isDirty || isReadOnly) return undefined

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      event.returnValue = ""
    }

    window.addEventListener("beforeunload", handleBeforeUnload)
    return () => window.removeEventListener("beforeunload", handleBeforeUnload)
  }, [isDirty, isReadOnly])

  useEffect(() => {
    if (!recordId || !hasInitialized || isReadOnly) return undefined

    if (!isDirty) {
      removeAssetEditDraft(recordId)
      return undefined
    }

    const timeoutId = window.setTimeout(() => {
      saveAssetEditDraft({
        recordId,
        assets,
        savedAt: new Date().toISOString(),
        baseUpdatedAt: assetUpdatedAt,
      })
    }, 300)

    return () => window.clearTimeout(timeoutId)
  }, [recordId, assets, isDirty, hasInitialized, isReadOnly, assetUpdatedAt])

  const handleCancel = () => {
    if (!isDirty) {
      navigate(recordId ? `/sapasst/view/${recordId}` : "/sapasst/history")
      return
    }

    Modal.confirm({
      title: "Discard unsaved changes?",
      content: "Changes are not saved yet. Leaving now will discard the current asset edits.",
      okText: "Discard",
      cancelText: "Stay",
      onOk: () => {
        if (recordId) {
          removeAssetEditDraft(recordId)
        }
        navigate(recordId ? `/sapasst/view/${recordId}` : "/sapasst/history")
      },
    })
  }

  const handleSubmit = async (comment: string) => {
    if (!recordId) {
      void message.error("Record ID is missing")
      return
    }

    if (!assets.length) {
      void message.error("Asset rows cannot be empty")
      return
    }

    if (isReadOnly) {
      void message.error("Only approver or PIC can edit this asset")
      return
    }

    try {
      setIsSubmitting(true)
      await updateSapAsset(recordId, {
        comment,
        assets,
      })
      removeAssetEditDraft(recordId)
      setInitialSnapshot(JSON.stringify({ assets }))
      setIsCommentModalVisible(false)
      commentForm.resetFields()
      void message.success("Asset updated successfully")
      navigate(`/sapasst/view/${recordId}`)
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error && "response" in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message || "Failed to update asset"
          : "Failed to update asset"
      void message.error(errorMessage)
    } finally {
      setIsSubmitting(false)
    }
  }

  const openCommentModal = () => {
    if (isReadOnly || !isDirty) return
    setIsCommentModalVisible(true)
  }

  const handleCommentConfirm = async () => {
    try {
      await commentForm.validateFields(["comment"])
      const rawCommentValue: unknown = commentForm.getFieldValue("comment")

      if (typeof rawCommentValue !== "string") {
        return undefined
      }

      await handleSubmit(rawCommentValue)
    } catch (_error) {
      return undefined
    }

    return undefined
  }

  if (isLoading && !currentAsset) {
    return (
      <TempLayout title="SAP - Edit Asset" navNavigateUrl="/sapasst/history">
        <div style={{ display: "flex", justifyContent: "center", padding: 50 }}>
          <Spin />
        </div>
      </TempLayout>
    )
  }

  return (
    <TempLayout title="SAP - Edit Asset" navNavigateUrl="/sapasst/history">
      {error && <Alert message="Error" description={error} type="error" showIcon style={{ marginBottom: 16 }} />}
      {!currentAsset ? (
        <Alert message="Not Found" description="Asset not found or ID missing" type="warning" showIcon />
      ) : (
        <Card>
          {isApproved && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 24 }}
              message="Approved asset cannot be edited"
              description="This page is read-only because the record is already APPROVED."
            />
          )}
          {!isApproved && !editAccess.canEdit && (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 24 }}
              message="Read-only access"
              description="Only approver or PIC can edit this asset."
            />
          )}

          <div style={{ width: "100%", maxWidth: "none" }}>
            <Collapse defaultActiveKey={["1"]} ghost style={{ marginBottom: 24, background: "#fff", borderRadius: 8 }}>
              <Collapse.Panel
                header={
                  <>
                    <UserOutlined /> User Information
                  </>
                }
                key="1"
              >
                <Descriptions column={{ xs: 1, sm: 2 }}>
                  <Descriptions.Item label="Employee ID">{currentAsset.employee_id}</Descriptions.Item>
                  <Descriptions.Item label="Fullname">{currentAsset.fullname}</Descriptions.Item>
                  <Descriptions.Item label="Level">{currentAsset.level || "-"}</Descriptions.Item>
                  <Descriptions.Item label="Department">{currentAsset.department || "-"}</Descriptions.Item>
                </Descriptions>
              </Collapse.Panel>
            </Collapse>

            <Collapse defaultActiveKey={["1"]} ghost style={{ marginBottom: 24, background: "#fff", borderRadius: 8 }}>
              <Collapse.Panel
                header={
                  <>
                    <FileTextOutlined /> Request Details
                  </>
                }
                key="1"
              >
                <Descriptions column={{ xs: 1, sm: 2 }}>
                  <Descriptions.Item label="Type of Request">{currentAsset.type_of_request || "-"}</Descriptions.Item>
                  <Descriptions.Item label="Approval Status">{currentAsset.approval_status || "-"}</Descriptions.Item>
                  <Descriptions.Item label="Note">{currentAsset.note || "-"}</Descriptions.Item>
                  <Descriptions.Item label="Branch">{currentAsset.branch || "-"}</Descriptions.Item>
                </Descriptions>
              </Collapse.Panel>
            </Collapse>

            <Collapse defaultActiveKey={["1"]} ghost style={{ marginBottom: 24, background: "#fff", borderRadius: 8 }}>
              <Collapse.Panel
                header={
                  <>
                    <TableOutlined /> Asset Data
                  </>
                }
                key="1"
              >
                {isNumberingMode && (
                  <Alert
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message="Asset numbering is per item"
                    description={
                      hasExpandedRows
                        ? "Rows with quantity greater than 1 were automatically split into one row per asset. All fields can still be edited manually. Excel copy-paste is optimized for Asset Number and Asset Subnumber, and multi-row paste only fills the selected item block."
                        : "All fields can be edited manually. Excel copy-paste is optimized for Asset Number and Asset Subnumber, and multi-row paste only fills the selected item block."
                    }
                  />
                )}
                <AssetPayloadEditor
                  items={assets}
                  disabled={isReadOnly}
                  templateKey={currentAsset.template_key}
                  numberingMode={isNumberingMode}
                  onChange={setAssets}
                />
              </Collapse.Panel>
            </Collapse>

            {!isReadOnly && (
              <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 16, borderWidth: 1, borderStyle: "solid" }}
                message="Review changes carefully before saving"
                description="Once the approval process is complete or the record is APPROVED, this asset can no longer be edited in the application."
              />
            )}

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 12, paddingTop: 8 }}>
              <Button onClick={handleCancel}>Cancel</Button>
              {!isReadOnly && (
                <Button type="primary" onClick={openCommentModal} loading={isSubmitting} disabled={!isDirty || isSubmitting} style={{ minWidth: 140 }}>
                  Save Changes
                </Button>
              )}
            </div>
          </div>
        </Card>
      )}

      <Modal
        title="Update Comment"
        open={isCommentModalVisible}
        okText="Submit Update"
        cancelText="Cancel"
        confirmLoading={isSubmitting}
        onCancel={() => {
          if (isSubmitting) return
          setIsCommentModalVisible(false)
          commentForm.resetFields()
        }}
        onOk={() => {
          void handleCommentConfirm()
        }}
      >
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="Final confirmation"
          description="Make sure the asset changes are correct. After the record is fully approved, editing will no longer be available in the application."
        />
        <Form form={commentForm} layout="vertical">
          <Form.Item
            label="Comment"
            name="comment"
            rules={[
              { required: true, message: "Comment is required" },
              { whitespace: true, message: "Comment is required" },
            ]}
          >
            <Input.TextArea rows={4} placeholder="Explain the asset update" maxLength={500} showCount />
          </Form.Item>
        </Form>
      </Modal>
    </TempLayout>
  )
}

export default SapAssetsEditPage
